import React, { FC } from 'react'
import FlatButton from '../../../components/buttons/flat_button'
import { SearchParams } from '../enterprises_search'
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { SelectInput, TextInput } from '../../../components/form'
import { EnterpriseCategoryValues } from '../../../enums/EnterpriseCategory'
import { BusinessCategoryParentValues } from '../../../enums/BusinessCategory'
import { PrefectureCategoryValues } from '../../../enums/PrefectureCategory'
import { SDGsCategoryValues } from '../../../enums/SDGsCategory'
import { CompanySizeValues } from '../../../enums/CompanySize'
import { CapitalSizeValues } from '../../../enums/CapitalSize'

type EnterpriseSearchFormProps = {
  register: UseFormRegister<SearchParams>
  watch: UseFormWatch<SearchParams>
  setValue: UseFormSetValue<SearchParams>
  getValues: UseFormGetValues<SearchParams>
  handleSearch: (searchParams: SearchParams) => void
  handleSubmit: UseFormHandleSubmit<SearchParams>
  toggleForm: () => void
  reset: () => void
}

const EnterpriseSearchForm: FC<EnterpriseSearchFormProps> = (props) => {
  return (
    <form action="submit" onSubmit={props.handleSubmit(props.handleSearch)}>
      <div className="space-y-6">
        {/* 検索条件：2行×3列のグリッド */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 1行目 */}
          {/* キーワード検索 */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              キーワード
            </label>
            <TextInput
              register={props.register('keyword')}
              placeholder="企業名、事業内容など"
              icon="search"
            />
          </div>

          {/* 企業カテゴリー */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              企業カテゴリー
            </label>
            <div className="relative">
              <SelectInput
                register={props.register('enterpriseCategory')}
                options={EnterpriseCategoryValues.map((item) => ({
                  value: item.code,
                  label: item.value,
                }))}
                placeholder="企業カテゴリーを選択"
                value={props.watch('enterpriseCategory')}
              />
            </div>
          </div>

          {/* ビジネスカテゴリー */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              ビジネスカテゴリー
            </label>
            <div className="relative">
              <SelectInput
                register={props.register('businessCategory')}
                options={BusinessCategoryParentValues.map((item) => ({
                  value: item.code,
                  label: item.value,
                }))}
                placeholder="ビジネスカテゴリーを選択"
                value={props.watch('businessCategory')}
              />
            </div>
          </div>

          {/* 2行目 */}
          {/* 所在地 */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              所在地
            </label>
            <div className="relative">
              <SelectInput
                register={props.register('location')}
                options={PrefectureCategoryValues.map((prefecture) => ({
                  value: prefecture,
                  label: prefecture,
                }))}
                placeholder="所在地を選択"
                value={props.watch('location')}
              />
            </div>
          </div>

          {/* 企業規模 */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              企業規模
            </label>
            <div className="relative">
              <SelectInput
                register={props.register('companySize')}
                options={CompanySizeValues.map((item) => ({
                  value: item.code,
                  label: item.value,
                }))}
                placeholder="企業規模を選択"
                value={props.watch('companySize')}
              />
            </div>
          </div>

          {/* 資本金 */}
          <div>
            <label className="block text-sm font-medium text-main-black mb-2">
              資本金
            </label>
            <div className="relative">
              <SelectInput
                register={props.register('capital')}
                options={CapitalSizeValues.map((item) => ({
                  value: item.code,
                  label: item.value,
                }))}
                placeholder="資本金規模を選択"
                value={props.watch('capital')}
              />
            </div>
          </div>
        </div>

        {/* SDGs目標選択 */}
        <div>
          <label className="block text-sm font-medium text-main-black mb-3">
            SDGs目標を選択
          </label>
          <div className="flex flex-wrap gap-2">
            {SDGsCategoryValues.map((goal, index) => (
              <button
                key={`sdgs-${index}`}
                type="button"
                onClick={() => {
                  if (props.watch('sdgsCategories').includes(goal.code)) {
                    props.setValue(
                      'sdgsCategories',
                      props
                        .watch('sdgsCategories')
                        .filter((s) => s !== goal.code)
                    )
                  } else {
                    props.setValue('sdgsCategories', [
                      ...props.watch('sdgsCategories'),
                      goal.code,
                    ])
                  }
                }}
                className={`px-3 py-2 text-xs rounded-md border transition-colors whitespace-nowrap ${
                  props.watch('sdgsCategories').includes(goal.code)
                    ? 'bg-main-green text-main-white border-main-green'
                    : 'bg-main-white text-main-bordergray border-main-bordergray hover:bg-main-lightgray'
                }`}
              >
                目標{goal.code}: {goal.value}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 検索ボタンと閉じるボタン */}
      <div className="flex justify-center items-center gap-4 mt-8">
        <FlatButton type="submit" className="!mx-0 w-auto px-8 min-w-[160px]">
          検索する
        </FlatButton>
        <FlatButton
          onClick={() => {
            props.reset()
          }}
          className="!mx-0 w-auto px-8 min-w-[160px]"
          outline={true}
        >
          リセット
        </FlatButton>
        <FlatButton
          onClick={props.toggleForm}
          className="!mx-0 w-auto px-8 !bg-main-darkgray min-w-[160px]"
        >
          閉じる
        </FlatButton>
      </div>
    </form>
  )
}

export default EnterpriseSearchForm
